// Global cache for permissions
let permissionsCache = null;

/**
 * Set the global permissions cache
 * @param {Array} permissions - Array of permission objects
 */
export const setPermissionsCache = (permissions) => {
  permissionsCache = permissions;
  console.log(`[Permissions] Cache set with ${permissions.length} permissions:`, 
    permissions.slice(0, 5).map(p => ({ id: p.id, name: p.name })), 
    '... and more'
  );
};

/**
 * Get a permission by ID
 * @param {number|string} id - The permission ID
 * @returns {Object|null} - The permission object or null if not found
 */
export const getPermissionById = (id) => {
  if (!permissionsCache) {
    // console.log('[Permissions] Cache is empty when looking for ID:', id);
    return null;
  }
  
  // Convert id to number to ensure consistent comparison
  const numericId = Number(id);
  
  // Log the type of ID being searched for debugging
  // console.log(`[Permissions] Looking for permission ID: ${id} (type: ${typeof id}, converted to: ${numericId})`);
  
  const permission = permissionsCache.find(p => Number(p.id) === numericId);
  
  if (!permission) {
    // console.log(`[Permissions] Permission ID ${id} not found in cache`);
  } else {
    // console.log(`[Permissions] Found permission for ID ${id}:`, permission.name);
  }
  
  return permission;
};

/**
 * Check if a user has permission for a specific URL
 * @param {Object} user - The user object from the session
 * @param {string} url - The URL to check permission for
 * @returns {boolean} - Whether the user has permission
 */
export const hasPermission = (user, url) => {
  //  console.log(`[Permissions] Checking permission for URL: ${url}`);
  

  
  // EMERGENCY FIX: Always allow access to quick-links
  if (url.includes('quick-links')) {
    // console.log('[Permissions] EMERGENCY FIX: Bypassing permission check for quick-links');
    return true;
  }
  
  //  console.log(`[Permissions] User permissionIds:`, user?.permissionIds);
  //  console.log(`[Permissions] Cache status:`, permissionsCache ? `Loaded with ${permissionsCache.length} permissions` : 'Not loaded');
  
  if (!user?.permissionIds || !permissionsCache) {
    //  console.log('[Permissions] No permissionIds or cache not loaded');
    return false;
  }

  // Clean the requested URL
  const cleanUrl = url.replace(/\/page$/, ''); // Remove /page suffix if present
  // console.log(`[Permissions] Clean URL: ${cleanUrl}`);

  // First, handle quick-links URL separately as a special case for debugging
  if (cleanUrl === '/panel/lms/utils/quick-links') {
    // console.log('[Permissions] Special handling for quick-links');
    const quickLinksPermission = permissionsCache.find(p => p.name === 'see_/panel/lms/utils/quick-links');
    
    if (quickLinksPermission) {
      // console.log('[Permissions] Found quick-links permission in cache:', quickLinksPermission);
      const userHasQuickLinks = user.permissionIds.some(id => Number(id) === Number(quickLinksPermission.id));
      // console.log(`[Permissions] User has quick-links permission: ${userHasQuickLinks}`);
      return userHasQuickLinks;
    }
  }

  // Regular permission check for other URLs
  const result = user.permissionIds.some(permissionId => {
    const permission = getPermissionById(permissionId);
    if (!permission) return false;
    
    // Remove 'see_' prefix and clean the permission URL
    const permissionUrl = permission.name.replace('see_', '').replace(/\/page$/, '');
    const match = cleanUrl === permissionUrl;
    
    if (match) {
      // console.log(`[Permissions] MATCH FOUND: Permission ${permission.name} (ID: ${permission.id}) matches URL ${cleanUrl}`);
    }
    
    return match;
  });
  
  // console.log(`[Permissions] Final result for ${url}: ${result}`);
  return result;
};

/**
 * Check if a user has a specific permission by name
 * @param {Object} user - The user object from the session
 * @param {string} permissionName - The permission name to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasNamedPermission = (user, permissionName) => {
  // Check if user and permissions are loaded
  if (!user?.permissionIds || !permissionsCache) {
    return false;
  }
  
  // Check if the user has the specific permission
  return user.permissionIds.some(permissionId => {
    const permission = getPermissionById(permissionId);
    return permission && permission.name === permissionName;
  });
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} user - The user object from the session
 * @param {Array<string>} permissionNames - Array of permission names to check
 * @returns {boolean} - Whether the user has any of the permissions
 */
export const hasAnyPermission = (user, permissionNames) => {
  // Check if user and permissions are loaded
  if (!user?.permissionIds || !permissionsCache) {
    return false;
  }
  
  // Check if the user has any of the specified permissions
  return user.permissionIds.some(permissionId => {
    const permission = getPermissionById(permissionId);
    return permission && permissionNames.includes(permission.name);
  });
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} user - The user object from the session
 * @param {Array<string>} permissionNames - Array of permission names to check
 * @returns {boolean} - Whether the user has all of the permissions
 */
export const hasAllPermissions = (user, permissionNames) => {
  // Check if user and permissions are loaded
  if (!user?.permissionIds || !permissionsCache) {
    return false;
  }
  
  // Check if the user has all of the specified permissions
  return permissionNames.every(permName => 
    user.permissionIds.some(permissionId => {
      const permission = getPermissionById(permissionId);
      return permission && permission.name === permName;
    })
  );
};

/**
 * Get all permissions for a user
 * @param {Object} user - The user object from the session
 * @returns {Array} - Array of permission objects
 */
export const getAllPermissions = (user) => {
  if (!user?.permissionIds || !permissionsCache) {
    return [];
  }
  
  return user.permissionIds.map(id => getPermissionById(id)).filter(Boolean);
}; 